import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableConfigDto } from '@asset-metrix/portal-gateway-client-js';
import { AsyncNotification } from '@asset-metrix/portal-ui';
import { useDisclosure } from '@mantine/hooks';
import { MutationStatus } from '@tanstack/react-query';

import { useWorkflowAction } from '../../hooks/useWorkflowAction';
import { invalidateTable } from '../../utils/queryCacheHelper';

import { WorkflowActionModalProps } from './WorkflowActionModal';

export type UseWorkflowActionModalReturnValue = {
  workflowActionModalProps?: WorkflowActionModalProps;
  open: (
    modalId: string,
    rowIds: string[],
    action: string,
    data: Record<string, unknown>[]
  ) => void;
  openReadonly: (modalId: string, data: Record<string, unknown>[]) => void;
};

type State = {
  modalId: string;
  rowIds: string[];
  action: string;
  data: Record<string, unknown>[];
  readOnly: boolean;
};

export function useWorkflowActionModalProperties(
  pageId: string,
  contentId: string,
  config: TableConfigDto
): UseWorkflowActionModalReturnValue {
  const { t } = useTranslation();
  const [isOpen, { open, close }] = useDisclosure();
  const [state, setState] = useState<State | null>(null);
  const [executionStatus, setExecutionStatus] = useState<MutationStatus[]>([]);
  const [mutationResult, setMutationResult] = useState<{
    status: MutationStatus;
    error: Error | null;
  }>({ status: 'idle', error: null });
  const { executeAction, reset: resetWorkflowAction } = useWorkflowAction();

  const formattingOptions = useMemo(() => {
    const options: WorkflowActionModalProps['formattingOptions'] = {};
    for (const column of config.columns) {
      options[column.key] = column;
    }
    return options;
  }, [config.columns]);

  const workflowModalSchemas = config.workflowModalSchemas;

  const workflowActionModalProps = useMemo<
    WorkflowActionModalProps | undefined
  >(() => {
    if (state == null) {
      return undefined;
    }

    const workflowModalSchema = workflowModalSchemas
      ? workflowModalSchemas[state.modalId]
      : undefined;

    if (workflowModalSchema?.modalType === 'documentSign') {
      return undefined;
    }

    const schema = workflowModalSchema?.jsonSchema;

    if (schema == null) {
      return undefined;
    }

    const resetStateAndClose = () => {
      setState(null);
      setExecutionStatus([]);
      setMutationResult({ status: 'idle', error: null });
      resetWorkflowAction();
      close();
    };

    return {
      closingSentence: state.readOnly
        ? undefined
        : workflowModalSchema?.closingSentence,
      introducingSentence: state.readOnly
        ? undefined
        : workflowModalSchema?.introducingSentence,
      isMultiRowModal: workflowModalSchema?.isMultiRowModal,
      opened: isOpen,
      schema,
      data: state.data,
      errorMessage: mutationResult.error?.message,
      workflowActionModalState: mutationResult.status,
      executionStatus,
      async onApply(userData, files) {
        if (state.readOnly) {
          return;
        }

        const requestIds = workflowModalSchema?.isMultiRowModal
          ? [...state.rowIds.entries()].filter(
              ([index]) => executionStatus[index] !== 'success'
            )
          : ([[0, state.rowIds]] as [number, string[]][]);

        const requestPromises = [];
        for (const [index, requestId] of requestIds) {
          setExecutionStatus((state) => {
            const nextState = [...state];
            nextState[index] = 'pending';
            return nextState;
          });

          requestPromises.push(
            executeAction(contentId, state.action, {
              rowIds: requestId,
              data: userData,
              files,
              modalId: state.modalId,
            })
              .then(() => {
                setExecutionStatus((state) => {
                  const nextState = [...state];
                  nextState[index] = 'success';
                  return nextState;
                });
              })
              .catch((error) => {
                setExecutionStatus((state) => {
                  const nextState = [...state];
                  nextState[index] = 'error';
                  return nextState;
                });
                throw error;
              })
          );
        }

        const widgetId = workflowModalSchema?.refreshAllTables
          ? undefined
          : contentId;

        try {
          setMutationResult({ status: 'pending', error: null });
          await Promise.all(requestPromises);
          setMutationResult({ status: 'success', error: null });
          AsyncNotification.show(
            { message: t('app.table.workflowActionModal.successMessage') },
            'success'
          );
          invalidateTable(config.cacheKey, pageId, widgetId);
          resetStateAndClose();
        } catch (error: any) {
          // Only invalidate table if at least one request succeeded
          const results = await Promise.allSettled(requestPromises);
          const hasAnySuccess = results.some(
            (result) => result.status === 'fulfilled'
          );
          if (hasAnySuccess) {
            invalidateTable(config.cacheKey, pageId, widgetId);
          }
          setMutationResult({ status: 'error', error });
        }
      },
      onClose() {
        resetStateAndClose();
      },
      fileUpload: state.readOnly
        ? undefined
        : workflowModalSchema?.documentUpload,
      formattingOptions,
      readOnly: state.readOnly,
    };
  }, [
    close,
    contentId,
    mutationResult.error,
    executeAction,
    isOpen,
    workflowModalSchemas,
    resetWorkflowAction,
    state,
    mutationResult.status,
    t,
    formattingOptions,
    executionStatus,
    config.cacheKey,
    pageId,
  ]);

  const openCallback = useCallback(
    (
      modalId: string,
      rowIds: string[],
      action: string,
      data: Record<string, unknown>[]
    ) => {
      setState({
        modalId,
        rowIds,
        action,
        data,
        readOnly: false,
      });
      setExecutionStatus(rowIds.map(() => 'idle'));
      open();
    },
    [open]
  );

  const openReadonlyCallback = useCallback(
    (modalId: string, data: Record<string, unknown>[]) => {
      setState({
        modalId,
        rowIds: [],
        action: '',
        data,
        readOnly: true,
      });
      setExecutionStatus([]);
      open();
    },
    [open]
  );

  return {
    open: openCallback,
    openReadonly: openReadonlyCallback,
    workflowActionModalProps,
  };
}
